import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import {
  Gavel, Download, Send, Clock, Check, MailCheck, Search, Trophy,
  AlertCircle, ChevronDown, ChevronUp, RefreshCw, FileText, Sparkles,
} from "lucide-react";

const STATUS_META = {
  drafted:       { label: "Drafted",       icon: FileText,  color: "bg-white/10 text-white/70 border-white/15" },
  sent:          { label: "Sent",          icon: Send,      color: "bg-blue-500/15 text-blue-300 border-blue-500/30" },
  acknowledged:  { label: "Acknowledged",  icon: MailCheck, color: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30" },
  investigating: { label: "Investigating", icon: Search,    color: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  resolved:      { label: "Resolved",      icon: Trophy,    color: "bg-green-500/15 text-green-300 border-green-500/30" },
};

const ALL_STEPS = ["drafted", "sent", "acknowledged", "investigating", "resolved"];

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
}

function StepDots({ currentStatus }) {
  const currentIdx = ALL_STEPS.indexOf(currentStatus);
  return (
    <div className="flex items-center gap-1.5 mt-2">
      {ALL_STEPS.map((s, i) => {
        const reached = i <= currentIdx;
        const isCurrent = i === currentIdx;
        return (
          <React.Fragment key={s}>
            <div
              className={`w-2 h-2 rounded-full transition-all ${
                reached ? "bg-[var(--if-gold)]" : "bg-white/15"
              } ${isCurrent ? "ring-2 ring-[var(--if-gold)]/40 scale-125" : ""}`}
              title={STATUS_META[s].label}
            />
            {i < ALL_STEPS.length - 1 && (
              <div className={`h-px w-4 ${reached && i < currentIdx ? "bg-[var(--if-gold)]" : "bg-white/10"}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function MarkSentForm({ disputeId, onDone, onCancel }) {
  const [tracking, setTracking] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/disputes/${disputeId}`, {
        status: "sent",
        sent_date: date,
        tracking_number: tracking || undefined,
      });
      onDone();
    } catch (err) {
      alert(err?.response?.data?.detail || "Couldn't mark as sent.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-3 p-3 rounded-lg border border-white/10 bg-black/20 space-y-3" data-testid={`mark-sent-form-${disputeId}`}>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="font-mono-label text-white/50 block mb-1">Date mailed</label>
          <input type="date" className="if-input !py-2 !text-xs" value={date}
            onChange={(e) => setDate(e.target.value)} data-testid="mark-sent-date" required />
        </div>
        <div>
          <label className="font-mono-label text-white/50 block mb-1">USPS tracking # (optional)</label>
          <input className="if-input !py-2 !text-xs" placeholder="9405..." value={tracking}
            onChange={(e) => setTracking(e.target.value)} data-testid="mark-sent-tracking" />
        </div>
      </div>
      <div className="flex gap-2">
        <button type="submit" disabled={busy} className="btn-gold !py-2 !px-4 !text-[11px]">
          <Check className="w-3.5 h-3.5" />{busy ? "Saving…" : "Confirm sent"}
        </button>
        <button type="button" onClick={onCancel} className="btn-outline !py-2 !px-4 !text-[11px]">Cancel</button>
      </div>
    </form>
  );
}

function DisputeCard({ d, onChange }) {
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showSentForm, setShowSentForm] = useState(false);

  const meta = STATUS_META[d.status] || STATUS_META.drafted;
  const Icon = meta.icon;

  const downloadPDF = async () => {
    setDownloading(true);
    try {
      const { data } = await api.get(`/disputes/${d.id}/pdf`);
      window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      alert("Couldn't generate the download link.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="card-dark p-4 transition-all" data-testid={`dispute-card-${d.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${meta.color}`}>
              <Icon className="w-3 h-3" />
              {meta.label}
            </span>
            <span className="text-[10px] text-white/45 uppercase tracking-wider" style={{ fontFamily: "Inter, sans-serif" }}>
              {d.bureau}
            </span>
            {d.mail_service === "premium" && (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[var(--if-gold)]/15 text-[var(--if-gold)] border border-[var(--if-gold)]/30" data-testid={`premium-badge-${d.id}`}>
                <Sparkles className="w-3 h-3" />
                Premium Mail
              </span>
            )}
          </div>
          <div className="font-display text-base text-white truncate" title={d.creditor}>
            {d.creditor}
            {d.account_last4 ? <span className="text-white/40 text-sm"> · ••{d.account_last4}</span> : null}
          </div>
          <div className="text-xs text-white/50 mt-0.5" style={{ fontFamily: "Inter, sans-serif" }}>
            {d.amount_usd != null ? `$${d.amount_usd.toLocaleString()} · ` : ""}Drafted {fmtDate(d.created_at)}
            {d.sent_date ? ` · Sent ${fmtDate(d.sent_date)}` : ""}
          </div>
          <StepDots currentStatus={d.status} />
        </div>
        <button
          onClick={() => setOpen(!open)}
          className="text-white/50 hover:text-white p-1.5 shrink-0"
          data-testid={`dispute-toggle-${d.id}`}
        >
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mt-3">
        <button
          onClick={downloadPDF}
          disabled={downloading}
          className="btn-outline !py-2 !px-3 !text-[11px]"
          data-testid={`dispute-pdf-${d.id}`}
        >
          <Download className="w-3.5 h-3.5" />
          {downloading ? "…" : "Download PDF"}
        </button>
        {d.status === "drafted" && d.mail_service !== "premium" && (
          <button
            onClick={() => setShowSentForm(!showSentForm)}
            className="btn-gold !py-2 !px-3 !text-[11px]"
            data-testid={`dispute-mark-sent-${d.id}`}
          >
            <Send className="w-3.5 h-3.5" />
            I mailed it
          </button>
        )}
        {d.status === "drafted" && d.mail_service === "premium" && (
          <span className="inline-flex items-center gap-1.5 text-[11px] text-[var(--if-gold)] px-2" style={{ fontFamily: "Inter, sans-serif" }}>
            <Clock className="w-3.5 h-3.5" />
            We'll mail it within 1-2 business days
          </span>
        )}
      </div>

      {showSentForm && (
        <MarkSentForm
          disputeId={d.id}
          onDone={() => { setShowSentForm(false); onChange(); }}
          onCancel={() => setShowSentForm(false)}
        />
      )}

      {open && (
        <div className="mt-4 pt-4 border-t border-white/10 space-y-3" data-testid={`dispute-detail-${d.id}`}>
          {d.tracking_number && (
            <div className="text-xs" style={{ fontFamily: "Inter, sans-serif" }}>
              <span className="text-white/50">USPS Tracking:</span>{" "}
              <span className="text-[var(--if-gold)]">{d.tracking_number}</span>
            </div>
          )}
          {d.outcome && (
            <div className="text-xs" style={{ fontFamily: "Inter, sans-serif" }}>
              <span className="text-white/50">Final outcome:</span>{" "}
              <span className="text-green-300 capitalize">{d.outcome.replace(/_/g, " ")}</span>
            </div>
          )}

          <div>
            <div className="font-mono-label text-white/45 mb-2">Timeline</div>
            <ol className="space-y-2">
              {(d.timeline || []).map((t, idx) => {
                const m = STATUS_META[t.status];
                const StepIcon = m?.icon || Clock;
                return (
                  <li key={`${t.at}-${idx}`} className="flex gap-2 text-xs" style={{ fontFamily: "Inter, sans-serif" }}>
                    <StepIcon className="w-3.5 h-3.5 text-[var(--if-gold)] mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <div className="text-white/85 capitalize">
                        {t.status.replace(/_/g, " ")}
                        <span className="text-white/40"> · {fmtDate(t.at)}</span>
                        <span className="text-white/40"> · {t.by}</span>
                      </div>
                      {t.note && <div className="text-white/55 italic">{t.note}</div>}
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      )}
    </div>
  );
}

export default function DisputesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const [postCheckoutMsg, setPostCheckoutMsg] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/disputes/me");
      setItems(data.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Listen for cross-component dispatches when a new dispute is generated from AnalysisCard
  useEffect(() => {
    const onCreated = () => load();
    window.addEventListener("dispute-created", onCreated);
    return () => window.removeEventListener("dispute-created", onCreated);
  }, []);

  // Handle return-from-Stripe (?premium_mail_session=...) — poll status until paid, then refresh list
  useEffect(() => {
    const sessionId = params.get("premium_mail_session");
    if (!sessionId) return;
    let cancelled = false;
    let attempts = 0;
    setPostCheckoutMsg("Confirming your payment…");

    const tick = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/disputes/premium-mail/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setPostCheckoutMsg(`Payment confirmed — ${data.disputes.length} letters queued for mailing.`);
          await load();
          // clean URL
          const next = new URLSearchParams(params);
          next.delete("premium_mail_session");
          setParams(next, { replace: true });
          setTimeout(() => setPostCheckoutMsg(""), 6000);
          return;
        }
        if (attempts < 20) {
          setTimeout(tick, 2000);
        } else {
          setPostCheckoutMsg("Still confirming payment — refresh in a moment.");
        }
      } catch {
        if (!cancelled && attempts < 5) setTimeout(tick, 3000);
      }
    };
    tick();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="card-dark p-6" data-testid="disputes-panel">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <Gavel className="w-4 h-4 text-[var(--if-gold)]" />
          <span className="font-mono-label text-[var(--if-gold)]">My Disputes</span>
        </div>
        <button
          onClick={load}
          className="text-white/40 hover:text-white p-1.5"
          data-testid="disputes-refresh"
          title="Refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {postCheckoutMsg && (
        <div className="mb-4 p-3 rounded-lg bg-[var(--if-gold)]/10 border border-[var(--if-gold)]/40 text-sm text-[var(--if-gold)] flex items-center gap-2" data-testid="premium-confirmation" style={{ fontFamily: "Inter, sans-serif" }}>
          <Sparkles className="w-4 h-4" />
          {postCheckoutMsg}
        </div>
      )}

      {loading && items.length === 0 ? (
        <div className="text-center py-6 text-xs text-white/40" style={{ fontFamily: "Inter, sans-serif" }}>Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-6 text-sm text-white/45 border border-dashed border-white/10 rounded-lg" style={{ fontFamily: "Inter, sans-serif" }}>
          No disputes yet. Generate one from any negative item in your credit-report analysis.
          <div className="mt-3 text-xs">
            <Link to="/profile" className="text-[var(--if-gold)] underline" data-testid="disputes-profile-link">
              Set your address first <AlertCircle className="w-3 h-3 inline" />
            </Link>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-3" data-testid="disputes-list">
          {items.map((d) => <DisputeCard key={d.id} d={d} onChange={load} />)}
        </div>
      )}
    </div>
  );
}
